import { FAVORITES_LIMIT } from 'favorite/constants/constants'
import { getCacheFavorites } from 'favorite/utils/getCacheFavorites'
import { IconErrorL } from 'icons/components/IconErrorL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useRouter } from 'next/navigation'
import { SnackbarStatus } from 'notifications/components/Snackbar/Snackbar'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext'
import { PageType } from 'types/analytics'

export function useNotifications(notification: boolean): {
	hasReachedLimit: () => Promise<boolean>
	notifyLimit: () => void
	notifyAndRemoveFavorite: (confirmRemove: () => Promise<void>) => void
} {
	const { t } = useLabels()
	const router = useRouter()
	const { addNotification } = useNotificationsContext()
	const {
		pageType,
		country: { countryISO, languageISO },
	} = useMasterData()

	const isInFavoritesPage = pageType === PageType.Favorites

	const hasReachedLimit = async () => {
		const favoritesFromCache = await getCacheFavorites()
		const favoritesSize = favoritesFromCache?.length

		return favoritesSize !== undefined && favoritesSize >= FAVORITES_LIMIT
	}

	const notifyLimit = () => {
		if (!notification) {
			return
		}

		const snackbarParams = isInFavoritesPage
			? {
					title: replacePlaceholders(t('wishlist.snackbar.limit.error.text'), {
						1: FAVORITES_LIMIT,
					}),
					icon: <IconErrorL color={'content-inverse'} />,
				}
			: {
					title: t('product.snackbar.wishlist.limit.error.title'),
					subtitle: replacePlaceholders(
						t('product.snackbar.wishlist.limit.error.subtitle'),
						{ 1: FAVORITES_LIMIT }
					),
					buttonText: t('error.review.button'),
					onClick: (_e: unknown) => {
						router.push(
							`/${countryISO}/${languageISO}/favorites`.toLocaleLowerCase()
						)
					},
				}

		addNotification({
			id: 'product.snackbar.limit.text',
			status: SnackbarStatus.INFO,
			...snackbarParams,
		})
	}

	/**
	 * Gives the user the opportunity to add back the favorite product they just removed.
	 * Only in the favorites page, since there it immediately disappears.
	 */
	const notifyAndRemoveFavorite = async (
		confirmRemove: () => Promise<void>
	) => {
		if (!notification || !isInFavoritesPage) {
			confirmRemove()
		} else {
			// The texts here will be later replaced by their respective labels,
			// they are just no published yet
			addNotification({
				id: 'product.snackbar.addBack.text',
				title: 'Eliminado de tus favoritos',
				status: SnackbarStatus.INFO,
				buttonText: 'Deshacer',
				onClose: confirmRemove,
				// onClick is required to show undo button
				onClick: () => {},
			})
		}
	}

	return { hasReachedLimit, notifyLimit, notifyAndRemoveFavorite }
}
